import { ContentCopy } from '@mui/icons-material';
import {
	Box,
	Button,
	Grid,
	SxProps,
	Theme,
	Tooltip,
	Typography,
	useTheme
} from '@mui/material';
import { useToast } from 'hooks';
import React from 'react';
import { ITheme } from 'types/theme';
import { ToastSeverity } from 'types/toast';
import { useBoolean, useCopyToClipboard } from 'usehooks-ts';

interface IAuditCardItemProps {
	field: string;
	value: string | Record<string, any>;
	valueSx?: SxProps<Theme>;
	ComponentValue: React.ComponentType<{ value: any }>;
}

const AuditCardItem: React.FC<IAuditCardItemProps> = ({
	field,
	value,
	valueSx,
	ComponentValue
}) => {
	const {
		value: copyVisible,
		setTrue: showCopy,
		setFalse: hideCopy
	} = useBoolean(false);
	const theme = useTheme<ITheme>();

	const { addToast } = useToast();
	const [, copyFn] = useCopyToClipboard();

	const copyFunc = React.useCallback(async () => {
		// Value may be json, so proper stringify is needed
		const copied = await copyFn(
			typeof value === 'string' ? value : JSON.stringify(value, null, 2)
		);
		if (copied) {
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('toast.value.copied.to.clipboard')
			});
		}
	}, [copyFn, value, addToast]);
	return (
		<Box
			pl={0.3}
			mt={2}
			data-testid="audit-card-item"
			onMouseEnter={showCopy}
			onMouseLeave={hideCopy}
		>
			<Typography variant="subtitle2" color={theme.palette.text.secondary}>
				{intl.tr(`fields.audits.column.${field}`)}
			</Typography>
			<Box>
				<Grid container>
					<Grid item sx={{ ...valueSx, maxWidth: '90%' }}>
						<ComponentValue value={value} />
					</Grid>
					<Grid item>
						{copyVisible && (
							<Button
								size="small"
								color="primary"
								onClick={copyFunc}
								sx={{
									padding: 0,
									minHeight: 0,
									minWidth: 0,
									marginLeft: 1
								}}
								data-testid="copy-audit-card-item-button"
							>
								<Tooltip
									title={intl.tr('audit.copy.item.tooltip')}
									placement="bottom-start"
								>
									<ContentCopy
										fontSize="small"
										color={theme.palette.secondary.main}
									/>
								</Tooltip>
							</Button>
						)}
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};

AuditCardItem.defaultProps = {
	valueSx: undefined
};

export default AuditCardItem;
